import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, GripHorizontal, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRIORITY_STYLE = {
  Critical: "border-red-500/40 bg-red-500/10 text-red-700",
  High: "border-amber-500/40 bg-amber-500/10 text-amber-700",
  Medium: "border-sky-500/40 bg-sky-500/10 text-sky-700",
  Low: "border-slate-200 bg-slate-100 text-slate-600",
};

/** Closed issues read as resolved; the current user's own issues stay prominent. */
function markerCardClass(issue, mine) {
  if (issue.status === "Closed")
    return "border-emerald-300 bg-emerald-50 hover:border-emerald-400";
  if (mine) return "border-orange-400 bg-orange-50 hover:border-orange-500";
  return "border-slate-300 bg-white hover:border-cyan-500/50";
}

/** Floating popup opened by clicking the orange "!" marker in the 3D viewer. */
export const IssueMarkerPopup = ({ projectId, panelUid, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [panelName, setPanelName] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const startDrag = (event) => {
    drag.current = { sx: event.clientX, sy: event.clientY, ox: pos.x, oy: pos.y };
    const move = (e) => {
      if (!drag.current) return;
      setPos({
        x: drag.current.ox + (e.clientX - drag.current.sx),
        y: drag.current.oy + (e.clientY - drag.current.sy),
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  useEffect(() => {
    if (!panelUid) return;
    api
      .get(`/projects/${projectId}/issues`)
      .then((res) => {
        const rows = (res.data.issues || []).filter((issue) => issue.panel_uid === panelUid);
        setIssues(rows);
        setPanelName(rows[0]?.panel_name || panelUid);
      })
      .catch((error) => toast.error(apiError(error)));
  }, [projectId, panelUid]);

  if (!panelUid) return null;

  return (
    <div
      data-testid="issue-marker-popup"
      style={{ transform: `translate(calc(-50% + ${pos.x}px), ${pos.y}px)` }}
      className="absolute left-1/2 top-24 z-40 w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl border border-orange-300 bg-[#DFE5EE] shadow-2xl"
    >
      <div
        data-testid="issue-marker-popup-handle"
        onMouseDown={startDrag}
        className="flex cursor-move items-center justify-between gap-2 border-b border-slate-300 px-4 py-3"
      >
        <GripHorizontal size={14} className="shrink-0 text-slate-400" />
        <div className="min-w-0">
          <p className="mono text-[10px] uppercase tracking-[0.2em] text-orange-600">
            Panel issues
          </p>
          <p className="mono truncate text-xs text-slate-800">{panelName}</p>
        </div>
        <button
          data-testid="close-issue-marker-popup"
          onClick={onClose}
          className="okn-tool grid h-7 w-7 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
        >
          <X size={15} />
        </button>
      </div>

      <div className="okn-scroll max-h-72 space-y-2 overflow-y-auto p-3">
        {issues.length === 0 ? (
          <p className="px-1 py-3 text-[11.5px] text-slate-500">No issues for this panel.</p>
        ) : (
          issues.map((issue) => {
            const mine = (issue.assignee_ids || []).includes(user?.id);
            return (
              <button
                key={issue.id}
                data-testid={`marker-issue-${issue.issue_key}`}
                onClick={() =>
                  navigate(`/projects/${projectId}/issues?issue=${issue.issue_key}`)
                }
                className={`w-full rounded-lg border p-3 text-left transition-colors ${markerCardClass(
                  issue,
                  mine
                )}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`mono text-[10px] ${
                      mine ? "font-semibold text-orange-700" : "text-slate-500"
                    }`}
                  >
                    {issue.issue_key}
                  </span>
                  {mine ? (
                    <span className="mono rounded bg-orange-500 px-1.5 text-[9px] uppercase tracking-wider text-white">
                      Mine
                    </span>
                  ) : null}
                  <span
                    className={`mono rounded border px-1.5 text-[9px] uppercase tracking-wider ${
                      PRIORITY_STYLE[issue.priority]
                    }`}
                  >
                    {issue.priority}
                  </span>
                  {issue.status === "Closed" ? (
                    <span className="mono ml-auto flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-800">
                      <CheckCircle2 size={12} className="text-emerald-700" /> Closed
                    </span>
                  ) : (
                    <span className="mono ml-auto text-[9px] uppercase tracking-wider text-slate-500">
                      {issue.status}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[12px] text-slate-900">{issue.title}</p>
                {issue.description ? (
                  <p className="mt-1 line-clamp-3 text-[11px] text-slate-600">
                    {issue.description}
                  </p>
                ) : null}
                <p className="mono mt-1.5 text-[9.5px] uppercase tracking-wider text-slate-500">
                  By {issue.created_by} → {issue.assigned_to_name || "Unassigned"}
                </p>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
